import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetBedRequestQuery } from "../services/bedRequestService";
import toast from "react-hot-toast";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Phone,
  Person,
  LocalHospital,
  Hotel,
  AccessTime,
  PriorityHigh,
  CheckCircle,
  Schedule,
} from "@mui/icons-material";
import BedRequestFormModal from "../components/BedRequestFormModal";

const BedRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    data: bedRequest,
    error,
    isFetching,
    refetch,
  } = useGetBedRequestQuery(id as string);

  const handleEditSuccess = () => {
    refetch();
    setIsModalOpen(false);
    toast.success("Bed Request updated successfully");
  };

  if (isFetching)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );

  if (error) return <Alert severity="error">Error fetching bed request</Alert>;

  if (!bedRequest.data) {
    return <Alert severity="error">Bed Request not found</Alert>;
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Bed Request Details
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Edit />}
          sx={{ ml: "auto" }}
          onClick={() => setIsModalOpen(true)}
        >
          Edit Bed Request
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid size={6}>
          <Card>
            <CardHeader title="Patient Information" />
            <CardContent>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <Person color="action" sx={{ mr: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography variant="subtitle2">Patient Name</Typography>
                  <Typography>
                    {bedRequest.data?.patientName || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={6}>
          <Card>
            <CardHeader title="Request Details" />
            <CardContent>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <LocalHospital color="action" sx={{ mr: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography variant="subtitle2">Hospital</Typography>
                  <Typography>
                    {bedRequest.data?.hospitalId?.name ||
                      bedRequest.data?.hospitalId ||
                      "N/A"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <Hotel color="action" sx={{ mr: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography variant="subtitle2">Bed Type</Typography>
                  <Typography>{bedRequest.data?.bedType || "N/A"}</Typography>
                </Box>
              </Box>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <PriorityHigh
                  color={
                    bedRequest.data?.urgencyLevel === "High"
                      ? "error"
                      : "action"
                  }
                  sx={{ mr: 2 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography variant="subtitle2">Urgency Level</Typography>
                  <Typography>
                    {bedRequest.data?.urgencyLevel || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader title="Status & Timeline" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={4}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Schedule color="action" sx={{ mr: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography variant="subtitle2">Requested At</Typography>
                      <Typography>
                        {formatDate(bedRequest.data?.requestedAt) || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AccessTime color="action" sx={{ mr: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography variant="subtitle2">Created At</Typography>
                      <Typography>
                        {formatDate(bedRequest.data?.createdAt) || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CheckCircle
                      color={
                        bedRequest.data?.status === "Approved"
                          ? "success"
                          : bedRequest.data?.status === "Rejected"
                          ? "error"
                          : "action"
                      }
                      sx={{ mr: 2 }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography variant="subtitle2">Status</Typography>
                      <Typography>
                        {bedRequest.data?.status || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <BedRequestFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleEditSuccess}
        bedRequest={bedRequest}
      />
    </Box>
  );
};

export default BedRequest;
